import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import { useAssetCardStoreV2 } from '@/stores/v2/AssetCardStore.ts';
import { useProjectsStoreV2 } from '@/stores/v2/ProjectStore.ts';
import { useJobsStore } from '@/stores/v2/JobsStore.ts';
import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';
import { useProjectRisks } from '@/stores/v2/ProjectRisks.ts';
import { useRequirementStorev2 } from '@/stores/v2/RequirementStore.ts';
import { useAgentTestStore } from '@/stores/v2/AgentTestStore.ts';
import { useTestingStore } from '@/stores/v2/TestingStore.ts';

import { BreadcrumbFactory } from '@/router/utils/breadcrumbFactory.ts';

import DefaultLayout from '@/layouts/DefaultLayout.vue';

// Legacy `/projects/...` URLs live on in bookmarks and notification emails, so they are
// redirected onto the `/ai-registry` structure instead of being dropped.
function paramToPath(value: string | string[] | undefined): string {
  if (Array.isArray(value)) return value.join('/');
  return value ?? '';
}

export const projectRoutes: RouteRecordRaw[] = [
  {
    path: '/ai-registry',
    name: 'ai-registry',
    meta: {
      layout: DefaultLayout,
      breadcrumb: BreadcrumbFactory.static.aiRegistry(),
      sidebarSection: 'ai-registry',
    },
    component: () => import('@/pages/Projects/AIRegistryPage.vue'),

    children: [
      {
        path: '',
        redirect: { name: 'projects' },
      },
      {
        path: 'ai-systems',
        name: 'projects',
        meta: { breadcrumb: BreadcrumbFactory.static.projects() },
        component: () => import('@/pages/Projects/ProjectsPage.vue'),
      },
      {
        path: 'my-submissions',
        name: 'my-submissions',
        meta: { breadcrumb: BreadcrumbFactory.static.mySubmissions() },
        component: () => import('@/pages/Projects/MySubmissionsPage.vue'),
      },
      {
        // Draft AI Systems reopen in the wizard; the detail page redirects them here.
        path: 'ai-systems/:id/edit',
        name: 'edit-ai-system',
        meta: { breadcrumb: BreadcrumbFactory.project(() => useProjectsStoreV2()) },
        component: () => import('@/pages/Projects/EditAISystemPage.vue'),
      },
      {
        path: 'ai-systems/:id',
        name: 'project-detail',
        meta: {
          layout: DefaultLayout,
          breadcrumb: BreadcrumbFactory.project(() => useProjectsStoreV2()),
          sidebarSection: 'project',
        },
        component: () => import('@/pages/Project/ProjectDetailPage.vue'),

        children: [
          {
            path: '',
            name: 'project-dashboard',
            component: () => import('@/pages/Project/pages/Dashboard/Dashboard.vue'),
          },
          {
            path: 'overview',
            name: 'project-overview',
            meta: { breadcrumb: BreadcrumbFactory.static.overview() },
            component: () => import('@/pages/Project/pages/Overview/OverviewPage.vue'),
          },
          {
            path: 'requirements',
            name: 'project-requirements',
            component: () => import('@/pages/Project/pages/Concepts/Requirements/Requirements.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.requirements(),
            },
            children: [
              {
                path: 'requirement-detail/:requirementId',
                name: 'requirement-detail',
                component: () => import('@/pages/Project/pages/Requirement/RequirementDetail.vue'),
                meta: {
                  breadcrumb: BreadcrumbFactory.requirement(() => ({
                    currentRequirement: useRequirementStorev2().currentRequirement,
                  })),
                },
              },
            ],
            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.governanceEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
          },
          {
            path: 'controls',
            name: 'project-controls',
            component: () => import('@/pages/Project/pages/Concepts/Controls/Controls.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.controls(),
            },
            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.governanceEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
          },
          {
            path: 'graph',
            name: 'project-graph',
            component: () => import('@/pages/Project/pages/Graph/GraphPage.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.graph(),
            },
            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.governanceEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
          },
          {
            path: 'settings',
            name: 'project-settings',
            component: () => import('@/pages/Project/pages/Settings/Settings.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.settings(),
            },
          },
          {
            path: 'risks',
            name: 'project-risks',
            component: () => import('@/pages/Project/pages/Risks/Risks/Risks.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.risks(),
            },
            children: [
              {
                path: ':riskId',
                name: 'risk-detail',
                component: () => import('@/pages/Project/pages/Risks/RiskDetail/RiskDetail.vue'),
                meta: {
                  breadcrumb: (route: RouteLocationNormalized) =>
                    BreadcrumbFactory.risk(() => useProjectRisks())(route),
                },
                children: [
                  {
                    path: 'quantification',
                    name: 'risk-quantification',
                    component: () =>
                      import('@/pages/Project/pages/Risks/Quantification/QuantificationPage.vue'),
                    meta: {
                      breadcrumb: BreadcrumbFactory.static.riskQuantification(),
                    },

                    beforeEnter: (to, from, next) => {
                      const organizationsStore = useOrganizationsStore();

                      if (organizationsStore.riskManagementEnabled) {
                        next();
                      } else {
                        next({ name: 'project-risks', params: { id: to.params.id } });
                      }
                    },
                  },
                  {
                    path: 'threats/:threatId/result',
                    name: 'threat-quantification-detail',
                    component: () =>
                      import('@/pages/Project/pages/Risks/ThreatQuantificationDetail/ThreatQuantificationDetail.vue'),
                    meta: {
                      breadcrumb: (route: RouteLocationNormalized) =>
                        BreadcrumbFactory.projectThreat(() => useProjectRisks())(route),
                    },

                    beforeEnter: (to, from, next) => {
                      const organizationsStore = useOrganizationsStore();

                      if (organizationsStore.riskManagementEnabled) {
                        next();
                      } else {
                        next({ name: 'project-risks', params: { id: to.params.id } });
                      }
                    },
                  },
                ],

                beforeEnter: (to, from, next) => {
                  const organizationsStore = useOrganizationsStore();

                  if (organizationsStore.riskManagementEnabled) {
                    next();
                  } else {
                    next({ name: 'project-dashboard', params: { id: to.params.id } });
                  }
                },
              },
            ],

            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.riskManagementEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
          },

          {
            path: 'evidence',
            name: 'project-evidence',
            component: () => import('@/pages/Project/pages/Evidence/Evidence.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.evidence(),
            },
            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.governanceEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
          },

          {
            path: 'assets',
            name: 'project-assets',
            component: () => import('@/pages/Project/pages/Assets/Assets.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.assets(),
            },
            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.governanceEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
            children: [
              {
                path: 'asset-detail/:assetId',
                name: 'asset-detail',
                component: () => import('@/pages/Project/pages/Assets/AssetDetailPage.vue'),
                meta: {
                  breadcrumb: BreadcrumbFactory.asset(() => useAssetCardStoreV2()),
                },
                beforeEnter: (to, from, next) => {
                  const organizationsStore = useOrganizationsStore();

                  if (organizationsStore.governanceEnabled) {
                    next();
                  } else {
                    next({ name: 'project-dashboard', params: { id: to.params.id } });
                  }
                },
              },
            ],
          },
          {
            path: 'runtime',
            name: 'project-runtime-inspection',
            component: () =>
              import('@/pages/Project/pages/RuntimeInspection/RuntimeInspection.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.testing(),
            },
            beforeEnter: (to, from, next) => {
              const organizationsStore = useOrganizationsStore();

              if (organizationsStore.governanceEnabled) {
                next();
              } else {
                next({ name: 'project-dashboard', params: { id: to.params.id } });
              }
            },
            children: [
              {
                path: 'test-detail/:testId',
                name: 'test-detail',
                component: () =>
                  import('@/pages/Project/pages/RuntimeInspection/pages/Testing/TestingDetailPage.vue'),
                meta: {
                  breadcrumb: BreadcrumbFactory.test(() => useTestingStore()),
                },
                beforeEnter: (to, from, next) => {
                  const organizationsStore = useOrganizationsStore();

                  if (organizationsStore.governanceEnabled) {
                    next();
                  } else {
                    next({ name: 'project-dashboard', params: { id: to.params.id } });
                  }
                },
              },
              {
                path: 'agent-test-detail/:agentTestId',
                name: 'agent-test-detail',
                component: () =>
                  import('@/pages/Project/pages/RuntimeInspection/pages/AgentTest/AgentTestDetailPage.vue'),
                meta: {
                  breadcrumb: BreadcrumbFactory.agentTest(() => useAgentTestStore()),
                },
                beforeEnter: (to, from, next) => {
                  const organizationsStore = useOrganizationsStore();

                  if (
                    organizationsStore.governanceEnabled &&
                    organizationsStore.agentTestsEnabled
                  ) {
                    next();
                  } else {
                    next({ name: 'project-dashboard', params: { id: to.params.id } });
                  }
                },
              },
            ],
          },
          {
            path: 'jobs',
            name: 'project-jobs',
            component: () => import('@/pages/Project/pages/Jobs/Jobs.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.static.jobs(),
            },
            children: [
              {
                path: 'job-detail/:jobId',
                name: 'job-detail',
                component: () => import('@/pages/Project/pages/Jobs/JobDetail/JobDetail.vue'),
                meta: {
                  breadcrumb: BreadcrumbFactory.job(() => useJobsStore()),
                },
              },
            ],
          },
          {
            path: 'testing',
            redirect: (to) => ({
              path: to.path.replace('/testing', '/runtime'),
              query: to.query,
            }),
            children: [
              {
                path: 'test-detail/:testId',
                redirect: (to) => ({
                  path: to.path.replace('/testing/', '/runtime/'),
                  query: to.query,
                }),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/projects',
    redirect: { name: 'projects' },
  },
  {
    path: '/projects/new-project',
    redirect: { name: 'projects' },
  },
  {
    path: '/projects/project-detail/:id/:rest(.*)*',
    redirect: (to) => {
      const rest = paramToPath(to.params.rest);

      return {
        path: `/ai-registry/ai-systems/${paramToPath(to.params.id)}${rest ? `/${rest}` : ''}`,
        query: to.query,
      };
    },
  },
];
